import ExpiryMap from 'expiry-map';
import pMemoize, { type AnyAsyncFunction, type CacheStorage } from 'p-memoize';

/**
 * By default, pMemoize only uses the first argument to determine the cache key.
 * This method turns all fn arguments into the cache key.
 */
export function cacheFnArguments(args) {
  return JSON.stringify(args);
}

/**
 * Memoizes an async function. The cache is an ExpiryMap with a default expiry of 5 minutes.
 * If you want to have access to the cache, you can pass it as the second argument.
 * This can be useful if you want to clear the cache.
 *
 * e.g.
 *
 * ```js
 * const cache = new ExpiryMap(5 * 60 * 1000);
 * const memoizedFn = memoize(asyncFn, cache);
 *
 * // Clear cache
 * cache.clear();
 * ```
 */
export function memoize<F extends AnyAsyncFunction>(
  fn: F,
  cache: CacheStorage<string, Awaited<ReturnType<F>>> | false = new ExpiryMap(5 * 60 * 1000)
): F {
  return pMemoize(fn, {
    cacheKey: cacheFnArguments,
    cache,
  });
}
